import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Chip, Stack } from '@mui/material';
import HeadTitle from '../../components/HeadTitle';
import Loader from '../../components/common/Loader/Loader';
import { getProfileActivations, getActivationCohortMembers } from '../../api/profileActivations';
import SAMPLE_DB from './sampleData';
import styles from './styles';

/**
 * Profile Activations tab.
 *
 * Native render (no iframe) scoped under `.favor-pa`, mirroring the Channel
 * Attribution / Messaging Activity / Subscription Report tabs. Ported from the
 * standalone mockup: newly ACTIVATED profiles (profileCompleted) split men vs
 * women, across three granularities (daily / weekly / monthly) with a bars/line
 * view toggle and headline KPIs.
 *
 * Live data: GET /admin/analytics/stat/getProfileActivations. On any failure the
 * tab falls back to the mockup sample so it never renders blank.
 */
/**
 * Which end of the (horizontally scrolling) charts to open on. Applies to BOTH the
 * activations chart and the cost-per-approved chart, so the two stay in step.
 * 'right' = most recent period first, i.e. today is in view without scrolling —
 * the user's call, 2026-07-22 (commit "scroll right").
 * 'left' = oldest period first, read left-to-right. Flip this one constant to change it;
 * the pin itself is direction-agnostic (see usePinChartEdge).
 */
const CHART_SCROLL_EDGE = 'right';

/**
 * Pin a horizontally scrolling chart wrap to CHART_SCROLL_EDGE whenever `dep` changes —
 * first paint, granularity switch, bars/line switch, series switch. Explicit rather than
 * relying on the browser's initial 0, because switching granularity otherwise keeps the
 * previous scrollLeft.
 *
 * ⚠️ A ONE-SHOT `el.scrollLeft = el.scrollWidth` HERE DOES NOT RELIABLY WORK, and fails
 * SILENTLY. If the wrap has no layout box at the moment the effect runs, `scrollWidth`
 * reads 0, so the assignment is a no-op and the pin is lost once layout arrives —
 * measured in Chrome: scrollWidth 0 at pin, 1660 after, scrollLeft still 0. That is why
 * an earlier right-edge version never appeared to do anything.
 * So: retry across a few frames until the element actually reports a scrollable width.
 * useLayoutEffect, not useEffect, so the common (already-laid-out) case lands before paint.
 */
const usePinChartEdge = (ref, dep) => {
  useLayoutEffect(() => {
    let raf = 0;
    let tries = 0;
    const pin = () => {
      const el = ref.current;
      // el can still be null on the cost panel, which only mounts once its data arrives.
      const max = el ? el.scrollWidth - el.clientWidth : 0;
      if (el && max > 0) { el.scrollLeft = CHART_SCROLL_EDGE === 'right' ? max : 0; return; }
      // Not laid out yet (or genuinely not scrollable). Try again next frame, but give up
      // rather than spin: 10 frames is ~160ms, far longer than a layout pass needs.
      tries += 1;
      if (tries < 10) raf = requestAnimationFrame(pin);
    };
    pin();
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, dep]);
};

const GRAN = {
  daily: { title: 'Still Active by Signup Day — Daily', meta: 'LAST 45 DAYS · LIVE STATUS' },
  weekly: { title: 'Still Active by Signup Day — Weekly', meta: 'LAST 13 WEEKS · WEEKS END SUNDAY · LIVE STATUS' },
  monthly: { title: 'Still Active by Signup Day — Monthly', meta: 'LAST 13 MONTHS · LIVE STATUS' },
};

const esc = (s) => String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const buildBars = (g, arr, hmax) => {
  const H = 176;
  const bar = (v, cls) => {
    const h = Math.max(v > 0 ? 3 : 0, Math.round((v / hmax) * H));
    return `<div class="gb ${cls}" style="height:${h}px"><span class="cnt">${v}</span></div>`;
  };
  const cols = arr.map((b) => {
    const lbl = b.lab + (b.sub ? `<small>${esc(b.sub)}</small>` : '');
    const tip = `${b.tip}: ${b.m + b.f} still active · Men ${b.m} · Women ${b.f} · signed up in this period and active (approved + hidden) right now`;
    return `<div class="mcol${b.cur ? ' cur' : ''}"><div class="mbars" title="${esc(tip)}">${bar(b.m, 'm')}${bar(b.f, 'f')}</div><div class="mlab">${lbl}</div></div>`;
  }).join('');
  return { className: `chart g-${g}`, html: cols };
};

const buildLine = (g, arr, hmax) => {
  const L = arr.length;
  const VW = g === 'daily' ? 1660 : 820;
  const Ht = 262; const pl = 40; const pr = 18; const pt = 16; const pb = 36;
  const X = (i) => pl + (i * (VW - pl - pr)) / (L - 1 || 1);
  const y1 = Math.ceil((hmax * 1.18) / 5) * 5 || 5;
  const Y = (v) => pt + (Ht - pt - pb) * (1 - v / y1);
  let svg = '';
  for (let t = 0; t <= 4; t += 1) {
    const gv = (y1 * t) / 4;
    const yy = Y(gv);
    svg += `<line x1="${pl}" y1="${yy}" x2="${VW - pr}" y2="${yy}" stroke="var(--line)" stroke-width="1"/>`
      + `<text x="${pl - 7}" y="${yy + 3}" text-anchor="end" font-size="9" fill="var(--txt-3)">${Math.round(gv)}</text>`;
  }
  [['m', 'var(--blue)', 'Men', -7], ['f', 'var(--rose)', 'Women', 14]].forEach((sr) => {
    const pts = arr.map((b, i) => `${X(i)},${Y(b[sr[0]])}`).join(' ');
    svg += `<polyline points="${pts}" fill="none" stroke="${sr[1]}" stroke-width="2.2" stroke-linejoin="round"/>`;
    arr.forEach((b, i) => {
      const v = b[sr[0]];
      const lastP = b.cur;
      svg += `<circle cx="${X(i)}" cy="${Y(v)}" r="${lastP ? 4 : 2.8}" fill="${sr[1]}"${lastP ? ' stroke="var(--gold)" stroke-width="2.5"' : ''}><title>${esc(b.tip)}: ${sr[2]} ${v}</title></circle>`
        + `<text x="${X(i)}" y="${Y(v) + sr[3]}" text-anchor="middle" font-size="8.5" font-weight="700" fill="${sr[1]}">${v}</text>`;
    });
  });
  arr.forEach((b, i) => {
    svg += `<text x="${X(i)}" y="${Ht - 22}" text-anchor="middle" font-size="9" fill="var(--txt-3)"${b.cur ? ' font-weight="700"' : ''}>${esc(b.lab)}</text>`;
    if (b.sub) svg += `<text x="${X(i)}" y="${Ht - 10}" text-anchor="middle" font-size="8.5" fill="var(--txt-3)">${esc(b.sub)}</text>`;
  });
  const min = g === 'daily' ? 1660 : 820;
  return { className: 'chart lview', html: `<svg viewBox="0 0 ${VW} ${Ht}" style="width:100%;min-width:${min}px;display:block" role="img" aria-label="Still active by signup day line chart">${svg}</svg>` };
};

const COST_GRAN = {
  daily: { title: 'Cost per Approved Member — Daily', meta: 'LAST 45 DAYS' },
  weekly: { title: 'Cost per Approved Member — Weekly', meta: 'LAST 13 WEEKS · WEEKS END SUNDAY' },
  monthly: { title: 'Cost per Approved Member — Monthly', meta: 'LAST 13 MONTHS' },
};

/** ₹12,345 — no decimals; this is a per-member cost, paise are noise. */
const inr = (v) => `₹${Math.round(v).toLocaleString('en-IN')}`;
/** Compact axis money: ₹1.2k / ₹12k. */
const inrAxis = (v) => (v >= 1000 ? `₹${(v / 1000).toFixed(v < 10000 ? 1 : 0)}k` : `₹${Math.round(v)}`);

/**
 * Cost-per-approved-member line chart.
 *
 * Differs from buildLine above in four ways that matter:
 *   1. NULL IS A GAP, NOT ZERO. A bucket with no approved members has an undefined cost per
 *      member, so its series breaks rather than diving to the axis — a 0 would draw
 *      "members were free that day". Each series is emitted as one polyline PER RUN of
 *      consecutive non-null points, with a hollow marker on isolated points that would
 *      otherwise be invisible (a run of length 1 has no line to draw).
 *   2. VALUE LABELS ONLY WHEN A SINGLE SERIES IS SELECTED. Three series x 45 points x
 *      4-digit rupee values is unreadable; with 'all' the numbers live in the tooltips.
 *   3. The y-axis is money, so gridlines are labelled compactly (₹1.2k).
 *   4. SPEND IS A FOURTH SERIES ('sp'), added 2026-07-25 at the user's request: its own
 *      pill beside Blended / Men / Women, and drawn in All. A ratio is a quotient — on a
 *      bucket whose whole cohort has since churned it is undefined and the line has nothing
 *      to show, which is exactly the bucket someone most wants to see the money on. Spend is
 *      unconditional, so a break in a ratio line beside a present spend series reads as
 *      "this much bought nothing", not "no data". Under All it shares ONE LOGARITHMIC ₹ axis
 *      with the three ratio lines — see the axis block below, which is load-bearing rather
 *      than stylistic: it is what makes the spend line provably the top line.
 */
const buildCostLine = (g, arr, sel) => {
  const L = arr.length;
  const VW = g === 'daily' ? 1660 : 820;
  // SPEND IS A FOURTH SELECTABLE SERIES ('sp'), sitting in the same pill row as Blended /
  // Men / Women and included in All. Selected on its own it owns the LEFT axis like any
  // other single series, so there is no second scale and no right gutter to reserve.
  const spendOnly = sel === 'sp';
  const withSpend = spendOnly || sel === 'all';
  const Ht = 262; const pl = 52; const pt = 16; const pb = 36;
  // No right gutter any more: there is no second axis to hold. Under All everything shares
  // ONE logarithmic ₹ axis on the left (see below), and every other pill is a single series
  // on a plain one.
  const pr = 18;
  const X = (i) => pl + (i * (VW - pl - pr)) / (L - 1 || 1);
  const FLOOR = pt + (Ht - pt - pb);

  const SPEND_COLOR = 'var(--green)';
  const SERIES = [
    ['o', 'var(--gold-line)', 'Blended'],
    ['m', 'var(--blue)', 'Men'],
    ['f', 'var(--rose)', 'Women'],
  ];
  // 'sp' is not in SERIES: it is not a cost-per-member ratio and shares neither their axis
  // nor their null rule, so it is drawn by its own block below rather than folded into the
  // loop that assumes both.
  const shown = sel === 'all' ? SERIES : SERIES.filter((s) => s[0] === sel);

  // Spend is always the period TOTAL, matching the pill's own label. It was the selected
  // gender's slice while it was a background layer; as a first-class series that would make
  // "Spend" mean three different amounts depending on which OTHER pill was last pressed.
  const spendOf = (b) => b.ct;

  let hmax = 1;
  arr.forEach((b) => shown.forEach((s) => { if (b[s[0]] != null) hmax = Math.max(hmax, b[s[0]]); }));
  let smax = 0;
  arr.forEach((b) => { smax = Math.max(smax, spendOf(b) || 0); });

  // ⚠️ ONE SHARED LOG AXIS UNDER 'all' (user's decision, 2026-07-25). Everything on this
  // plot is rupees, so one axis is the honest arrangement — but spend is ~40x a per-member
  // cost, and on a LINEAR shared axis the cost lines collapse: measured on live data, Men
  // peaks at 4px of 210 on the daily view and 0px on weekly, Blended at 25px and 1px. The
  // two-axis version that avoided that had its own defect, which is what this replaces: two
  // scales mean HEIGHT STOPS MEANING VALUE, and on 27 + 30 Jun the Women line drew above the
  // spend line, reading as "one woman cost more than the day's entire budget" — impossible
  // (`cf <= ct`, `sf >= 1`), reported as a data bug, and expensive to explain every time.
  //
  // A log axis is the only arrangement that keeps BOTH properties: it is monotone, so
  // spend — which mathematically contains every ratio drawn beside it — can never plot
  // below one of them; and it gives ₹18 and ₹16,456 comparable room on the same ruler.
  // The price is that equal vertical distances are equal MULTIPLES, not equal rupees, so
  // slopes read as growth rates. The axis is labelled per decade and the panel blurb says
  // so; if that wording goes, the chart starts lying about the size of its own movements.
  //
  // Single-series pills keep a plain linear axis: with nothing to compare against there is
  // no ordering to protect, and linear is easier to read.
  const useLog = sel === 'all';
  const y1 = spendOnly
    ? (Math.ceil((smax * 1.12) / 5) * 5 || 5)
    : (Math.ceil((hmax * 1.18) / 5) * 5 || 5);

  // Log domain: a decade floor below the smallest positive value on the plot, up to a
  // little above the largest. Values of 0 are REAL here (₹0 per member happens when a
  // gender drew no spend at all that day) but have no log; they clamp to the floor, which
  // is where a linear axis would have drawn them anyway.
  let lmin = Infinity;
  arr.forEach((b) => {
    shown.forEach((s) => { const v = b[s[0]]; if (v != null && v > 0) lmin = Math.min(lmin, v); });
    const sp = spendOf(b) || 0;
    if (withSpend && sp > 0) lmin = Math.min(lmin, sp);
  });
  const logHi = Math.max(hmax, withSpend ? smax : 0) * 1.15 || 10;
  const logLo = Number.isFinite(lmin) ? 10 ** Math.floor(Math.log10(lmin)) : 1;
  const lSpan = Math.log10(logHi) - Math.log10(logLo) || 1;
  const YLOG = (v) => pt + (Ht - pt - pb)
    * (1 - (Math.log10(Math.max(v, logLo)) - Math.log10(logLo)) / lSpan);

  const Y = useLog ? YLOG : (v) => pt + (Ht - pt - pb) * (1 - v / y1);
  // With one axis there is no second mapping: spend is drawn by the same Y as everything
  // else. That identity IS the fix — nothing can be plotted on a scale of its own again.
  const SY = Y;

  let svg = '';
  if (useLog) {
    // Gridlines on the decades (₹10, ₹100, ₹1k, ₹10k) rather than five even steps: the
    // whole point of the scale is that even steps in rupees are not even steps in height.
    for (let d = Math.log10(logLo); d <= Math.log10(logHi) + 1e-9; d += 1) {
      const gv = 10 ** d;
      const yy = Y(gv);
      svg += `<line x1="${pl}" y1="${yy}" x2="${VW - pr}" y2="${yy}" stroke="var(--line)" stroke-width="1"/>`
        + `<text x="${pl - 7}" y="${yy + 3}" text-anchor="end" font-size="9" fill="var(--txt-3)">${esc(inrAxis(gv))}</text>`;
    }
    svg += `<text x="${pl - 7}" y="${pt - 5}" text-anchor="end" font-size="8.5" fill="var(--txt-3)" opacity="0.85">log ×10</text>`;
  } else {
    for (let t = 0; t <= 4; t += 1) {
      const gv = (y1 * t) / 4;
      const yy = Y(gv);
      svg += `<line x1="${pl}" y1="${yy}" x2="${VW - pr}" y2="${yy}" stroke="var(--line)" stroke-width="1"/>`
        + `<text x="${pl - 7}" y="${yy + 3}" text-anchor="end" font-size="9" fill="var(--txt-3)">${esc(inrAxis(gv))}</text>`;
    }
  }

  // ---- spend series ----------------------------------------------------------------
  //
  // A plain line in both modes now that there is only one axis. Under 'all' it is the
  // TOP line by construction, not by luck: every ratio drawn beside it is a slice of that
  // same money divided by at least one member (`cf <= ct`, `sf >= 1`), and a log axis is
  // monotone, so it cannot plot below them. That ordering is the whole reason this panel
  // has one axis — it is what stops "women cost more than the entire day's spend" from
  // being readable off a chart where it is arithmetically impossible.
  //
  // It does NOT break where the ratio lines do: zero spend is a true zero (no money went
  // out), unlike a zero cost per member, which is undefined. So the line runs on across the
  // buckets a ratio gaps, which is exactly what makes it worth drawing — that is the bucket
  // someone most wants the money for — and the hollow ring marks those buckets on it.
  if (withSpend && smax > 0) {
    const slot = (VW - pl - pr) / (L - 1 || 1);
    const pts = arr.map((b, i) => ({ x: X(i), y: SY(spendOf(b) || 0) }));
    svg += `<polyline points="${pts.map((p) => `${p.x},${p.y}`).join(' ')}" fill="none" stroke="${SPEND_COLOR}" stroke-width="${spendOnly ? 2.2 : 1.9}" stroke-linejoin="round"/>`;

    // One transparent full-height band per bucket, so the spend is hoverable ANYWHERE in its
    // column rather than only on the stroke — the buckets worth hovering are exactly the
    // ones where the ratio line is absent and there is no dot to aim at. Drawn before the
    // ratio series, so a ratio dot still wins the hover where the two overlap.
    arr.forEach((b, i) => {
      const sp = spendOf(b) || 0;
      const parts = [`${b.tip} · Spend: ${sp > 0 ? inr(sp) : 'nothing'}`];
      if (b.st != null) parts.push(b.st > 0 ? `${b.st} member${b.st === 1 ? '' : 's'} still active` : 'NOBODY still active');
      if (b.at != null && b.st != null && b.at > b.st) parts.push(`${b.at} acquired at the time`);
      if (sp > 0 && b.st > 0) parts.push(`${inr(sp / b.st)} each`);
      svg += `<rect x="${X(i) - slot / 2}" y="${pt}" width="${slot}" height="${FLOOR - pt}" fill="transparent" pointer-events="all"><title>${esc(parts.join(' · '))}</title></rect>`;
      const py = SY(sp);
      if (sp > 0 && b.st === 0) {
        // Money out, nobody left — ringed on the spend line, the same bucket the ratio
        // series marks with `!` on the floor.
        svg += `<circle cx="${X(i)}" cy="${py}" r="4" fill="var(--card)" stroke="${SPEND_COLOR}" stroke-width="2"/>`;
      } else if (spendOnly) {
        svg += `<circle cx="${X(i)}" cy="${py}" r="${b.cur ? 4 : 2.8}" fill="${SPEND_COLOR}"${b.cur ? ' stroke="var(--gold)" stroke-width="2.5"' : ''}/>`;
      }
      if (spendOnly && sp > 0) {
        svg += `<text x="${X(i)}" y="${py - 7}" text-anchor="middle" font-size="8.5" font-weight="700" fill="${SPEND_COLOR}">${esc(inrAxis(sp))}</text>`;
      }
    });
  }

  shown.forEach((sr) => {
    const [key, color, name] = sr;
    // Split into runs of consecutive non-null points so gaps stay gaps.
    let run = [];
    const flush = () => {
      if (run.length > 1) {
        svg += `<polyline points="${run.map((p) => `${p.x},${p.y}`).join(' ')}" fill="none" stroke="${color}" stroke-width="2.2" stroke-linejoin="round"/>`;
      } else if (run.length === 1) {
        // Lone point between two gaps — draw a ring so it is not lost.
        svg += `<circle cx="${run[0].x}" cy="${run[0].y}" r="3.4" fill="none" stroke="${color}" stroke-width="2"/>`;
      }
      run = [];
    };
    arr.forEach((b, i) => {
      const v = b[key];
      if (v == null) { flush(); return; }
      run.push({ x: X(i), y: Y(v) });
    });
    flush();

    // A null with REAL SPEND behind it is not an empty day — it is the worst kind of day:
    // money out, nobody left. Drawn as a hollow ring ON THE AXIS with a caret, so it reads
    // as "spent, returned nothing" instead of vanishing into the gap it shares with
    // genuinely-nothing days.
    //
    // These became COMMON on 2026-07-25, when the denominator moved to the live still-approved
    // read: any day whose whole cohort has since churned now lands here, and the older half
    // of the window is full of them. That is the honest consequence of the definition, not a
    // rendering fault — but it is why the marker names the acquired count, so a day that
    // bought 5 members and kept none is distinguishable from one that bought nobody at all.
    arr.forEach((b, i) => {
      if (b[key] != null) return;
      const spend = key === 'o' ? b.ct : (key === 'm' ? b.cm : b.cf);
      if (!spend) return; // genuinely nothing happened — a real gap
      // FLOOR, not Y(0): a log axis has no zero, and the marker belongs on the axis line in
      // either mode. On the linear axis the two are the same pixel.
      const yy = FLOOR;
      const acq = key === 'o' ? b.at : (key === 'm' ? b.am : b.af);
      const warnTip = esc(`${b.tip} · ${name}: ${inr(spend)} spent${acq ? `, ${acq} member${acq === 1 ? '' : 's'} acquired` : ''}, none still active today — cost per member is undefined, not zero`);
      svg += `<circle cx="${X(i)}" cy="${yy}" r="4.2" fill="var(--card)" stroke="${color}" stroke-width="2" stroke-dasharray="2.2 1.8"><title>${warnTip}</title></circle>`
        + `<text x="${X(i)}" y="${yy - 9}" text-anchor="middle" font-size="9" font-weight="700" fill="${color}">!</text>`;
    });

    arr.forEach((b, i) => {
      const v = b[key];
      if (v == null) return;
      const lastP = b.cur;
      const spend = key === 'o' ? b.ct : (key === 'm' ? b.cm : b.cf);
      // The denominator IS the bar above, by construction — same query, same gate (see the
      // survivors arm in loadCostInputs). Saying so in the tooltip is what lets a reader
      // check the division against the chart above instead of assuming the panels disagree.
      const n = key === 'o' ? b.st : (key === 'm' ? b.sm : b.sf);
      // The disclosure underneath is what that spend originally BOUGHT (`at`/`am`/`af`,
      // deleted members included), because the plotted figure alone hides two things:
      // it is a live read that keeps rising as the cohort churns, and it says nothing
      // about how many members the money actually acquired on the day.
      const acq = key === 'o' ? b.at : (key === 'm' ? b.am : b.af);
      let tip = `${b.tip} · ${name}: ${inr(v)} per member still active (${inr(spend)} / ${n} member${n === 1 ? '' : 's'} — that is the bar above)`;
      if (acq != null && acq > n) {
        tip += ` · that spend originally bought ${acq}; ${acq - n} have since left or deleted, so this was ${inr(spend / acq)} per member acquired and re-prices upward as more of them go`;
      }
      svg += `<circle cx="${X(i)}" cy="${Y(v)}" r="${lastP ? 4 : 2.8}" fill="${color}"${lastP ? ' stroke="var(--gold)" stroke-width="2.5"' : ''}><title>${esc(tip)}</title></circle>`;
      if (sel !== 'all') {
        svg += `<text x="${X(i)}" y="${Y(v) - 7}" text-anchor="middle" font-size="8.5" font-weight="700" fill="${color}">${esc(inr(v))}</text>`;
      }
    });
  });

  arr.forEach((b, i) => {
    svg += `<text x="${X(i)}" y="${Ht - 22}" text-anchor="middle" font-size="9" fill="var(--txt-3)"${b.cur ? ' font-weight="700"' : ''}>${esc(b.lab)}</text>`;
    if (b.sub) svg += `<text x="${X(i)}" y="${Ht - 10}" text-anchor="middle" font-size="8.5" fill="var(--txt-3)">${esc(b.sub)}</text>`;
  });

  const min = g === 'daily' ? 1660 : 820;
  return { className: 'chart lview', html: `<svg viewBox="0 0 ${VW} ${Ht}" style="width:100%;min-width:${min}px;display:block" role="img" aria-label="Cost per active member line chart">${svg}</svg>` };
};

const Pills = ({ options, value, onChange }) => (
  <div className="periods">
    {options.map(([v, label]) => (
      <button key={v} type="button" className={v === value ? 'on' : ''} onClick={() => onChange(v)}>{label}</button>
    ))}
  </div>
);

/** The calendar date a cohort cell refers to: the cohort's own day plus the offset. */
const dateOfCell = (iso, k) => {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + k)).toISOString().slice(0, 10);
};

/**
 * Sum of a cohort's activations up to day k, capped at the first 7 days — the design's
 * `Math.min(k, 6)`: sign-ups convert within the first week.
 */
const cumAct = (c, k) => (c.na || []).slice(0, Math.min(k, 6) + 1).reduce((a, b) => a + b, 0);

/**
 * Cohort retention grid — 60 daily cohorts, one row each, reading across to how many of
 * that cohort were still active N days later.
 *
 * Two bases (the design's toggle):
 *   act  — the row is everyone who ACTIVATED that day, so Day 0 is 100% by definition.
 *   sign — the row is everyone who SIGNED UP that day, ghosts included; the count can
 *          climb as late activations land before attrition takes over.
 *
 * Shorter `act` arrays are cohorts whose later days haven't happened yet — those cells
 * stay blank rather than reading a misleading 0.
 *
 * Clicks are delegated from the table rather than bound per cell: at 60x60 that would be
 * thousands of closures rebuilt on every toggle.
 */
const CohortTable = ({ rows, basis, pct, onCell, recordedFrom }) => {
  const sgn = basis === 'sign';
  const maxD = rows.reduce((n, c) => Math.max(n, c.act.length), 0);
  const cols = Array.from({ length: maxD }, (_, k) => k);
  const baseOf = (c) => (sgn ? c.S : c.act[0]);
  // Average the UNROUNDED shares and round once at the end — rounding each cohort first
  // and averaging those would drift the column by a point.
  // null = that day predates the status log, so it is UNKNOWN — skip it rather than let
  // it count as a zero and drag the average down.
  const avg = cols.map((k) => {
    const ps = rows
      .filter((c) => k < c.act.length && c.act[k] !== null && baseOf(c))
      .map((c) => (100 * c.act[k]) / baseOf(c));
    return ps.length ? Math.round(ps.reduce((x, y) => x + y, 0) / ps.length) : null;
  });

  const handleClick = (e) => {
    if (!onCell) return;
    const td = e.target.closest('td[data-k]');
    if (!td) return;
    onCell(rows[Number(td.dataset.ci)], Number(td.dataset.k));
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <table className="ctbl" onClick={handleClick}>
      <thead>
        <tr>
          <th className="rh">{sgn ? 'Signed up' : 'Activated on'}</th>
          {cols.map((k) => <th key={k}>{k === 0 ? 'Day 0' : `D${k}`}</th>)}
        </tr>
      </thead>
      <tbody>
        {rows.map((c, ci) => {
          const base = baseOf(c);
          const activated = sgn ? cumAct(c, c.act.length - 1) : 0;
          // LABELS ARE LOAD-BEARING — these two numbers are DIFFERENT metrics and were
          // repeatedly mistaken for each other (and for Channel Attribution's columns):
          //   "ever activated" = cumulative, historical, INCLUDES members since deleted.
          //                      Has NO counterpart on Channel Attribution.
          //   "still active" = live snapshot of canonical status `approved`. This is the
          //                      one that equals Channel Attribution's Approved column
          //                      (NOT its Active total, which adds Hidden — status v3).
          // Verified 19 Jul 2026: 389 signed up · 145 ever activated (106 live + 39
          // deleted) · 58 still active — and CA showed 389 / 58. Do not shorten these
          // back to "activated" / "approved now".
          const sub = sgn
            ? `${c.S.toLocaleString()} signed up · ${activated.toLocaleString()} ever activated${c.activeNow != null ? ` · ${c.activeNow} still active` : ''}`
            : `${c.size.toLocaleString()} activated${c.activeNow != null ? ` · ${c.activeNow} still active` : ''}`;
          const subTip = sgn
            ? `${c.lab}: ${c.S.toLocaleString()} signed up. `
              + `${activated.toLocaleString()} activated within 7 days (cumulative — includes members since deleted). `
              + `${c.activeNow} are still active right now (profile complete, visible on search or not) — `
              + `this is the figure that matches Channel Attribution's "Approved" (not its Active total).`
            : `${c.lab}: ${c.size.toLocaleString()} activated. ${c.activeNow} still active right now.`;
          return (
            <tr key={c.iso}>
              <td className="rh" title={subTip}>{c.lab}<small>{sub}</small></td>
              {cols.map((k) => {
                if (k >= c.act.length) return <td key={k} />;
                const v = c.act[k];
                if (v === null) {
                  return (
                    <td key={k} className="nr" title={`${c.lab} · Day ${k}: not recorded`}>—</td>
                  );
                }
                const p = base ? Math.round((100 * v) / base) : 0;
                // A cell before the status log existed is an UPPER BOUND, not a count: the
                // exits that carry no date (admin hides, suspensions, profile regressions)
                // are invisible to it, so it assumes those members are still here. Marked
                // "<=" and hatched so it can never be read as a measurement.
                const bounded = k > 0 && recordedFrom && dateOfCell(c.iso, k) < recordedFrom;
                const tip = bounded
                  ? `${c.lab} · Day ${k}: AT MOST ${v} still active (${p}% of ${base}) — an upper bound. `
                    + `${c.dateless} of this cohort's departures carry no date, so they are assumed still here. `
                    + `The true figure is between ${c.activeNow} and ${v}. Status has only been recorded since ${recordedFrom}.`
                  : `${sgn ? 'Signed up ' : 'Activated '}${c.lab} · Day ${k}: ${v} active (${p}% of ${base})${onCell ? ' · click for movement' : ''}`;
                // Day 0 of an activation cohort is the baseline — flat highlight, not heat.
                const isD0 = k === 0 && !sgn;
                return (
                  <td
                    key={k}
                    data-ci={ci}
                    data-k={k}
                    className={`${onCell ? 'ck' : ''}${isD0 ? ' d0' : ''}`.trim()}
                    title={tip}
                    style={isD0 ? undefined : { background: `rgba(31,157,107,${((Math.min(p, 100) / 100) * 0.55).toFixed(3)})` }}
                  >
                    {pct ? `${p}%` : v.toLocaleString()}
                  </td>
                );
              })}
            </tr>
          );
        })}
        <tr className="avg">
          <td className="rh">Average</td>
          {cols.map((k) => <td key={k} className={avg[k] === null ? 'nr' : undefined}>{avg[k] === null ? '—' : `${avg[k]}%`}</td>)}
        </tr>
      </tbody>
    </table>
  );
};

/**
 * Movement summary for the clicked cell. Unlike the mockup — which derived "went
 * inactive" as `prev + newlyActivated - cur` and so assumed nobody ever returns — these
 * are real counts from the server, including the members who CAME BACK. Without that
 * third flow the arithmetic doesn't close on real data.
 */
const CellDetail = ({ detail }) => {
  if (!detail) return null;
  const { lab, day, loading, error, data, cumNa } = detail;
  if (loading) return <div className="cdetail">Loading movement for {lab} · Day {day}…</div>;
  if (error) return <div className="cdetail">Couldn’t load the movement for {lab} · Day {day}: {error}</div>;
  const { counts, cur, na, base, dayIso, basis } = data;
  const sgn = basis === 'sign';
  if (data.recorded === false) {
    return (
      <div className="cdetail">
        <b>{lab} {sgn ? 'signup' : 'activation'} cohort — Day {day} ({dayIso}):</b> not recorded. Account status is
        only stored as it is now, so who was active on a past day cannot be reconstructed. Status changes have been
        recorded since {data.recordingSince ? String(data.recordingSince).slice(0, 10) : 'the recorder first ran'} —
        days from then on are real.
      </div>
    );
  }
  const net = counts.net;
  return (
    <div className="cdetail">
      <b>{lab} {sgn ? 'signup' : 'activation'} cohort — Day {day} ({dayIso}):</b> {cur.toLocaleString()} active
      {day > 0 ? (
        <>
          {' · '}<span className="cg">+{counts.newlyActivated}</span> newly activated
          {' · '}<span className="cb">+{counts.returned}</span> came back
          {' · '}<span className="cr">−{counts.wentInactive}</span> left the active set
          {` · net ${net >= 0 ? '+' : '−'}${Math.abs(net)} vs Day ${day - 1}`}
        </>
      ) : (
        <>
          {sgn
            ? <> · <span className="cg">+{na}</span> activated on signup day itself, of {base.toLocaleString()} who signed up</>
            : ' · the cohort itself: everyone counted here activated this day'}
        </>
      )}
      {sgn && cumNa != null && (
        <> · activated so far: {cumNa.toLocaleString()} of {base.toLocaleString()} ({base ? Math.round((100 * cumNa) / base) : 0}% conversion)</>
      )}
    </div>
  );
};

const MS_MARKS = [1, 3, 7, 14, 30, 45, 59];

/** Attrition curves — overall (dashed grey) vs men (blue) vs women (rose). */
const buildAttrSvg = (A) => {
  const L = Math.min(A.o.length, A.m.length, A.f.length);
  const VW = 960; const Ht = 282; const pl = 46; const pr = 84; const pt = 14; const pb = 32;
  const X = (k) => pl + (k * (VW - pl - pr)) / (L - 1 || 1);
  const Y = (p) => pt + (Ht - pt - pb) * (1 - p / 100);
  let svg = '';
  [0, 25, 50, 75, 100].forEach((gv) => {
    const yy = Y(gv);
    svg += `<line x1="${pl}" y1="${yy}" x2="${VW - pr}" y2="${yy}" stroke="var(--line)" stroke-width="1"/>`
      + `<text x="${pl - 8}" y="${yy + 3}" text-anchor="end" font-size="9" fill="var(--txt-3)">${gv}%</text>`;
  });
  const marks = MS_MARKS.filter((k) => k < L);
  const series = [['o', '#9b978f', 'Overall', '5 4', 0], ['m', 'var(--blue)', 'Men', null, 14], ['f', 'var(--rose)', 'Women', null, -8]];
  // null means "no cohort's status log covers this day-offset" — NOT 0%. The API is
  // explicit about emitting null rather than 0 for exactly this reason, so a gap must be
  // drawn as a gap. Y(null) coerces to Y(0), which planted the curve on the axis and read
  // as "everyone churned" (live on 2026-07-20: attr.m[59] is null, and 59 IS a mark).
  // Split the polyline into runs of consecutive real values instead.
  series.forEach((sr) => {
    const arr = A[sr[0]];
    let run = [];
    const flush = () => {
      if (run.length > 1) {
        svg += `<polyline points="${run.join(' ')}" fill="none" stroke="${sr[1]}" stroke-width="2.2"${sr[3] ? ` stroke-dasharray="${sr[3]}"` : ''} stroke-linejoin="round"/>`;
      }
      run = [];
    };
    arr.slice(0, L).forEach((v, k) => { if (v == null) flush(); else run.push(`${X(k)},${Y(v)}`); });
    flush();
    if (sr[4] !== 0) {
      marks.forEach((k) => {
        if (arr[k] == null) return; // no dot, no label on an unmeasured day
        svg += `<circle cx="${X(k)}" cy="${Y(arr[k])}" r="3" fill="${sr[1]}"><title>Day ${k}: ${arr[k]}% of ${sr[2].toLowerCase()} still active</title></circle>`
          + `<text x="${X(k)}" y="${Y(arr[k]) + sr[4]}" text-anchor="middle" font-size="8.5" font-weight="700" fill="${sr[1]}">${arr[k]}%</text>`;
      });
    }
  });
  // End-of-line series labels, stacked apart. All three curves converge to near zero by
  // day 59 on real data, so placing each label at its own endpoint overprints them into
  // an unreadable smudge — push them apart to a minimum gap, keeping their order.
  // 14 units, not 11: a 10px label measures ~11.5 viewBox units tall, so anything under
  // that still touches (measured in Chrome, not guessed).
  const LABEL_GAP = 14;
  // Anchor each label to that series' LAST MEASURED point, not index L-1 — which may be
  // null (see the gap note above) and would drag the label to the 0% axis.
  const lastReal = (arr) => {
    for (let k = Math.min(L, arr.length) - 1; k >= 0; k -= 1) if (arr[k] != null) return arr[k];
    return null;
  };
  const ends = series
    .map((sr) => ({ name: sr[2], colour: sr[1], v: lastReal(A[sr[0]]) }))
    .filter((e) => e.v != null)
    .map((e) => ({ ...e, y: Y(e.v) }))
    .sort((a, b) => a.y - b.y);
  let floor = -Infinity;
  ends.forEach((e) => { e.ly = Math.max(e.y, floor + LABEL_GAP); floor = e.ly; });
  ends.forEach((e) => {
    // Leader line when the label had to move off its own endpoint.
    if (Math.abs(e.ly - e.y) > 1.5) {
      svg += `<line x1="${X(L - 1) + 2}" y1="${e.y}" x2="${X(L - 1) + 7}" y2="${e.ly - 3}" stroke="${e.colour}" stroke-width="1"/>`;
    }
    svg += `<text x="${X(L - 1) + 9}" y="${e.ly + 3}" font-size="10" font-weight="700" fill="${e.colour}">${e.name}</text>`;
  });
  marks.concat([0]).forEach((k) => {
    svg += `<text x="${X(k)}" y="${Ht - 10}" text-anchor="middle" font-size="9" fill="var(--txt-3)">${k === 0 ? 'D0' : `D${k}`}</text>`;
  });
  return `<svg viewBox="0 0 ${VW} ${Ht}" style="width:100%;min-width:860px;display:block" role="img" aria-label="Attrition curves">${svg}</svg>`;
};

/** Plain-language read of the men-vs-women gap, so the panel states its own conclusion. */
const AttritionVerdict = ({ attr }) => {
  const at = (g, k) => (attr[g] && attr[g][k] != null ? attr[g][k] : null);
  const d7 = 7 < attr.o.length ? 7 : attr.o.length - 1;
  const d30 = 30 < attr.o.length ? 30 : attr.o.length - 1;
  const m30 = at('m', d30); const f30 = at('f', d30);
  // Don't state a conclusion off unmeasured days. This used to coerce null to 0, which
  // produced a confident "Men are falling faster … 0% still active" from missing data.
  if (m30 == null || f30 == null || at('m', d7) == null || at('f', d7) == null) {
    return (
      <div className="verdict">
        <b>Not enough recorded history yet.</b> Status history only covers part of this
        window, so the men-vs-women gap can&apos;t be stated for Day&nbsp;{d7}/Day&nbsp;{d30} yet.
      </div>
    );
  }
  const [fast, slow, fast7, slow7, fast30, slow30] = m30 < f30
    ? ['Men', 'Women', at('m', d7), at('f', d7), m30, f30]
    : ['Women', 'Men', at('f', d7), at('m', d7), f30, m30];
  return (
    <div className="verdict">
      <b>{fast} are falling faster.</b> A week after activating, {fast7}% of {fast.toLowerCase()} are still active
      vs {slow7}% of {slow.toLowerCase()}; by Day {d30} the gap is {fast30}% vs {slow30}% — a difference
      of {Math.abs(f30 - m30).toFixed(1)}%.
    </div>
  );
};

const AttritionTable = ({ attr }) => {
  const marks = MS_MARKS.filter((k) => k < attr.o.length);
  // null = that day-offset isn't covered by the status log. Rendering it as `${null}%`
  // printed a literal "null%"; an em dash is the same "not recorded" convention the
  // cohort grid already uses for uncovered cells.
  const row = (cls, name, arr) => (
    <tr key={name}>
      <td className={cls}>{name}</td>
      {marks.map((k) => (
        <td key={k} className={cls} title={arr[k] == null ? 'Not recorded — no status history covers this day' : undefined}>
          {arr[k] == null ? '—' : `${arr[k]}%`}
        </td>
      ))}
    </tr>
  );
  return (
    <table className="atbl">
      <thead>
        <tr><th>Still active</th>{marks.map((k) => <th key={k}>{`D${k}`}</th>)}</tr>
      </thead>
      <tbody>
        {row('df', 'Women', attr.f)}
        {row('dm', 'Men', attr.m)}
        {row('', 'Overall', attr.o)}
        <tr className="dif">
          <td>Gap (Women − Men)</td>
          {marks.map((k) => {
            // A gap needs BOTH sides measured. `0 - null` is 0 in JS, which printed a
            // confident "+0.0%" for a day nobody has data on.
            if (attr.f[k] == null || attr.m[k] == null) {
              return <td key={k} title="Not recorded — no status history covers this day">—</td>;
            }
            const d = attr.f[k] - attr.m[k];
            return <td key={k}>{`${d >= 0 ? '+' : ''}${d.toFixed(1)}%`}</td>;
          })}
        </tr>
      </tbody>
    </table>
  );
};

const ProfileActivations = () => {
  const [DB, setDB] = useState(null);
  const [flag, setFlag] = useState('loading'); // loading | live | placeholder
  const [gran, setGran] = useState('daily');
  const [viewMode, setViewMode] = useState('line');
  // Which cost line(s) the cost panel draws. 'all' by default: the men-vs-women gap is the
  // whole point of the panel, and it is only visible with both drawn together.
  const [costSeries, setCostSeries] = useState('all'); // all | o (blended) | m | f
  const [cohortView, setCohortView] = useState('n'); // n = counts | p = percent
  // Opens on the SIGNUP basis (user's call). The mockup defaults to 'act' (`cbasis:'act'`),
  // but signup-date is the view that answers "of everyone who signed up that day, how many
  // completed and how many are still here" — the question this tab exists for.
  const [cohortBasis, setCohortBasis] = useState('sign'); // sign = by signup date | act = by activation date
  const [cohortGender, setCohortGender] = useState('o'); // o = overall | m | f
  const [detail, setDetail] = useState(null);
  const hasData = useRef(false);
  const detailSeq = useRef(0);
  // The chart is wider than its container (1660px vs ~1188px for 45 daily points), and
  // `.chartwrap` scrolls horizontally. Which end it opens on is CHART_SCROLL_EDGE.
  // The cost-per-approved chart below it is the same width and gets the same treatment.
  const chartWrapRef = useRef(null);
  const costWrapRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await getProfileActivations();
      if (!data) throw new Error('empty');
      setDB(data);
      hasData.current = true;
      setFlag('live');
    } catch (err) {
      if (!hasData.current) { setDB(SAMPLE_DB); setFlag('placeholder'); }
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const kpis = useMemo(() => {
    if (!DB) return null;
    const k = DB.kpi;
    const d7 = k.p7 ? (100 * (k.l7 - k.p7)) / k.p7 : 0;
    const d30 = k.p30 ? (100 * (k.l30 - k.p30)) / k.p30 : 0;
    const wTot = k.w30m + k.w30f;
    return { k, d7, d30, womenShare: wTot ? Math.round((100 * k.w30f) / wTot) : 0 };
  }, [DB]);

  const chart = useMemo(() => {
    if (!DB) return null;
    const arr = DB[gran] || [];
    let hmax = 1; let tm = 0; let tf = 0;
    arr.forEach((b) => { hmax = Math.max(hmax, b.m, b.f); tm += b.m; tf += b.f; });
    const built = viewMode === 'bars' ? buildBars(gran, arr, hmax) : buildLine(gran, arr, hmax);
    const tot = tm + tf;
    return { ...built, tm, tf, tot };
  }, [DB, gran, viewMode]);

  /**
   * Cost per approved member, on the same granularity toggle as the activations chart.
   * Absent from the payload on older server builds, so the panel is conditional.
   *
   * Window totals are recomputed from the buckets ON SCREEN rather than read from
   * cost.meta — meta is always the 45-day daily window, so on the weekly/monthly views it
   * would print a total that does not match the line being looked at.
   */
  const costChart = useMemo(() => {
    if (!DB || !DB.cost || !DB.cost[gran]) return null;
    const arr = DB.cost[gran];
    const t = arr.reduce((a, b) => ({
      cm: a.cm + b.cm, cf: a.cf + b.cf, cu: a.cu + b.cu, ct: a.ct + b.ct,
      am: a.am + b.am, af: a.af + b.af, at: a.at + b.at,
      // Late completers and survivors ride along for the same reason as the money: the
      // footer states both, and meta's copies are always the 45-day daily window.
      // `|| 0` on all four — an older server build returns buckets without them.
      lt: a.lt + (b.lt || 0),
      sm: a.sm + (b.sm || 0), sf: a.sf + (b.sf || 0), st: a.st + (b.st || 0),
      // Fallback-day count travels with the buckets for the same reason the money does:
      // meta's copy is always the 45-day window and would misreport the other two views.
      nr: a.nr + (b.nr || 0),
    }), {
      cm: 0, cf: 0, cu: 0, ct: 0, am: 0, af: 0, at: 0, lt: 0, sm: 0, sf: 0, st: 0, nr: 0,
    });
    const built = buildCostLine(gran, arr, costSeries);
    return {
      ...built,
      ...t,
      // Window-level ratios: summed cost over summed members, NOT the mean of the daily
      // ratios — averaging ratios would weight a 2-member day like a 60-member day. The
      // denominator is the STILL-APPROVED count (t.st/sm/sf), matching the plotted series and
      // the bars above; t.at/am/af price the same money per member acquired, in the footer.
      vo: t.st ? Math.round(t.ct / t.st) : null,
      vm: t.sm ? Math.round(t.cm / t.sm) : null,
      vf: t.sf ? Math.round(t.cf / t.sf) : null,
      ao: t.at ? Math.round(t.ct / t.at) : null,
      unallocPct: t.ct ? (100 * t.cu) / t.ct : 0,
      noRatioDays: t.nr,
    };
  }, [DB, gran, costSeries]);

  // Both charts open on the same edge (today in view), and both re-pin every time their
  // own series is rebuilt. See usePinChartEdge for why this is not a one-shot assignment.
  usePinChartEdge(chartWrapRef, chart);
  usePinChartEdge(costWrapRef, costChart);

  const today = useMemo(() => new Date().toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }), []);

  const cohortRows = DB && DB.coh && DB.coh[cohortBasis] ? DB.coh[cohortBasis][cohortGender] : null;
  const attrSvg = useMemo(() => (DB && DB.attr ? buildAttrSvg(DB.attr) : null), [DB]);
  // Changing what the table shows invalidates any open movement summary.
  useEffect(() => { setDetail(null); }, [cohortBasis, cohortGender]);

  /**
   * A cell click does two things, per the design: the movement summary lands under the
   * table, and the full member list opens in its own tab (same pattern as the Daily
   * Actives "Online now" click-through). window.open must be called synchronously in the
   * gesture or the popup blocker eats it — so it goes before the await.
   */
  const onCell = useCallback(async (row, day) => {
    const q = new URLSearchParams({
      basis: cohortBasis, gender: cohortGender, iso: row.iso, day: String(day),
    }).toString();
    window.open(`/activation-cohort-day?${q}`, '_blank', 'noopener,noreferrer');

    const seq = detailSeq.current + 1;
    detailSeq.current = seq;
    const cumNa = cohortBasis === 'sign' ? cumAct(row, day) : null;
    setDetail({ lab: row.lab, day, loading: true, cumNa });
    try {
      const data = await getActivationCohortMembers({ basis: cohortBasis, gender: cohortGender, iso: row.iso, day });
      if (detailSeq.current !== seq) return; // a newer click already won
      setDetail({ lab: row.lab, day, data, cumNa });
    } catch (err) {
      if (detailSeq.current !== seq) return;
      setDetail({ lab: row.lab, day, error: err.message || 'request failed', cumNa });
    }
  }, [cohortBasis, cohortGender]);

  return (
    <Box sx={{ pb: 4 }}>
      <style>{styles}</style>

      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
        <HeadTitle
          imageSrc="/assets/images/admin-panel/analytics.png"
          text="Profile Activations"
          description="New profiles activated, men vs women — daily, weekly and monthly."
        />
        {flag === 'placeholder' && (
          <Chip size="small" color="warning" variant="outlined" label="Showing sample data — live source unavailable" />
        )}
      </Stack>

      <div className="favor-pa">
        {!DB ? (
          <div style={{ minHeight: 'calc(100vh - 240px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Loader />
          </div>
        ) : (
          <>
            <Stack direction="row" justifyContent="flex-end" sx={{ mb: 1 }}>
              <Pills
                options={[['daily', 'Daily · 45D'], ['weekly', 'Weekly'], ['monthly', 'Monthly']]}
                value={gran}
                onChange={setGran}
              />
            </Stack>
            <div className="ctx">{today} &middot; today, this week and this month are in progress</div>

            {/* KPIs */}
            <div className="stats">
              <div className="stat"><span className="bar" /><div className="k">Joined Today &middot; Still Active</div>
                <div className="v">{(kpis.k.today.m + kpis.k.today.f).toLocaleString()}</div>
                <div className="d">{`Men ${kpis.k.today.m} · Women ${kpis.k.today.f} · so far today`}</div>
              </div>
              <div className="stat sg"><span className="bar" /><div className="k">Last 7 Days</div>
                <div className="v">{kpis.k.l7.toLocaleString()}<span className={`delta ${kpis.d7 >= 0 ? 'up' : 'dn'}`}>{`${kpis.d7 >= 0 ? '+' : ''}${kpis.d7.toFixed(1)}%`}</span></div>
                <div className="d" title="Both figures are live status reads, so the older window has had a week longer to churn. The comparison is biased downward.">{`vs ${kpis.k.p7.toLocaleString()} the 7 days before · older window has churned longer`}</div>
              </div>
              <div className="stat sb"><span className="bar" /><div className="k">Last 30 Days</div>
                <div className="v">{kpis.k.l30.toLocaleString()}<span className={`delta ${kpis.d30 >= 0 ? 'up' : 'dn'}`}>{`${kpis.d30 >= 0 ? '+' : ''}${kpis.d30.toFixed(1)}%`}</span></div>
                <div className="d" title="Both figures are live status reads, so the older window has had a month longer to churn. The comparison is biased downward.">{`vs ${kpis.k.p30.toLocaleString()} the 30 days before · older window has churned longer`}</div>
              </div>
              <div className="stat sr"><span className="bar" /><div className="k">Women Share &middot; 30D</div>
                <div className="v">{`${kpis.womenShare}%`}</div>
                <div className="d">{`Men ${kpis.k.w30m.toLocaleString()} · Women ${kpis.k.w30f.toLocaleString()} in the last 30 days`}</div>
              </div>
            </div>

            {/* Chart */}
            <div className="panel">
              <div className="phead">
                <h2>{GRAN[gran].title}</h2>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  <Pills options={[['bars', 'Bars'], ['line', 'Line']]} value={viewMode} onChange={setViewMode} />
                  <span className="pmeta">{GRAN[gran].meta}</span>
                </div>
              </div>
              <div className="psub">
                Each pair of bars is one period <b>dated by signup</b>: blue = men, rose = women, with the count
                above every bar. The gold outline marks the period in progress. A bar counts the members who
                signed up in that period and are <b>active &mdash; approved + hidden &mdash; right now</b> &mdash; so it
                <b>falls as that cohort churns</b>, and a member who finished their profile days after signing up
                still counts on their signup day. ⚠️ Because of that, <b>older periods are not comparable with
                recent ones</b>: they read lower mainly because they have had longer to lose people, not because
                fewer joined. Each bar equals its row&apos;s <b>still active</b> figure in the cohort table below on the
                <b>By signup date</b> basis &mdash; the <i>last</i> cell of the row, not its Day 0. Day 0 is the fixed
                count of who activated that day and never changes; the bar is what is left of them today.
              </div>
              <div className="legend">
                <span><i className="sw" style={{ background: 'var(--blue)' }} />Men <b>{chart.tm.toLocaleString()}</b> ({chart.tot ? Math.round((100 * chart.tm) / chart.tot) : 0}%)</span>
                <span><i className="sw" style={{ background: 'var(--rose)' }} />Women <b>{chart.tf.toLocaleString()}</b> ({chart.tot ? Math.round((100 * chart.tf) / chart.tot) : 0}%)</span>
              </div>
              <div className="chartwrap" ref={chartWrapRef}><div className={chart.className} dangerouslySetInnerHTML={{ __html: chart.html }} /></div>
              <div className="totline">Total in view: <b>{chart.tot.toLocaleString()}</b> members still active &middot; Men <b>{chart.tm.toLocaleString()}</b> &middot; Women <b>{chart.tf.toLocaleString()}</b></div>
            </div>

            {/* Cost per approved member — ad spend on a day divided by the members who
                signed up that day and completed their profile, whenever they completed it
                and whatever their status is now (redefined 2026-07-24, WEB-66). Sits
                directly under the activations chart and follows the same granularity
                toggle.

                Two things this panel must keep saying out loud, because both are easy to
                misread off the line alone:
                  * its denominator is FAR above the bar above it — by design, not drift.
                    This divides by everyone the spend bought; the bar counts only those
                    still active today (1,330 of 4,000 over the 45-day window). The blurb
                    and the tooltip both name the survivor count and identify it AS the
                    bar, and if either goes the two panels start looking like they
                    contradict each other;
                  * Men + Women now equal Blended exactly, but only because spend with no
                    gender target anywhere is PRO-RATED into them (2026-07-23). Adding up
                    is not the same as being measured, and the split now looks equally
                    solid whether it was read off the platform or assumed. The assumed
                    share is therefore printed, not left for someone to discover — if that
                    caveat line ever goes, the number silently becomes an opinion. */}
            {costChart && (
              <div className="panel">
                <div className="phead">
                  <h2>{COST_GRAN[gran].title}</h2>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Pills
                      options={[['all', 'All'], ['o', 'Blended'], ['m', 'Men'], ['f', 'Women'], ['sp', 'Spend']]}
                      value={costSeries}
                      onChange={setCostSeries}
                    />
                    <span className="pmeta">{COST_GRAN[gran].meta}</span>
                  </div>
                </div>
                <div className="psub">
                  <b>All</b> ad spend in each period — every campaign, every platform — divided by the members who
                  <b> signed up in it and are still active (approved + hidden) right now</b>. Deleted and departed
                  members are dropped, so the denominator is <b>exactly the bar above</b>, day for day.
                  <b> Men</b> and <b>Women</b> split by the gender each campaign was actually targeted at on the ad
                  platform, falling back to the campaign name, and together they <b>sum to Blended</b>.
                  <b> This is a live figure, so it re-prices the past:</b> the spend is fixed but the members are
                  counted today, so every past point drifts <b>upward</b> as that cohort churns and periods can only
                  be compared with that in mind. What the money originally bought is in the footer and every
                  tooltip &mdash; {costChart.at.toLocaleString()} members acquired in this view against
                  {' '}{costChart.st.toLocaleString()} still here. <b>Spend</b> is a series of its own — the money
                  itself, not a rate. In <b>All</b> all four share <b>one ₹ axis on a log scale</b>, where each
                  gridline is <b>10&times;</b> the one below: that keeps ₹18 and ₹16,000 both readable, and it
                  guarantees the spend line sits <b>above</b> every cost line, which it must, since each of those is
                  a slice of that same money divided between members. Read heights as multiples, not as rupees
                  apart. Pick a single series for a plain axis. A period that returned nobody still shows its money:
                  the cost line breaks and is marked <b>!</b> (undefined, not zero) while the spend line runs on,
                  ringed at that point.
                </div>
                <div className="legend">
                  <span><i className="sw" style={{ background: 'var(--gold-line)' }} />Blended <b>{costChart.vo == null ? '—' : inr(costChart.vo)}</b></span>
                  <span><i className="sw" style={{ background: 'var(--blue)' }} />Men <b>{costChart.vm == null ? '—' : inr(costChart.vm)}</b></span>
                  <span><i className="sw" style={{ background: 'var(--rose)' }} />Women <b>{costChart.vf == null ? '—' : inr(costChart.vf)}</b></span>
                  {/* The spend series carries no cost-per-member figure by nature — it is
                      the numerator on its own — so this entry states the window's total
                      spend instead of a rate, and names the axis it is read on, which
                      changes with the pill (left when Spend is selected alone). */}
                  <span>
                    <i className="sw" style={{ background: 'var(--green)' }} />
                    Spend{costSeries === 'all' ? ' (log axis)' : ''} <b>{inr(costChart.ct)}</b> in view
                  </span>
                </div>
                <div className="chartwrap" ref={costWrapRef}><div className={costChart.className} dangerouslySetInnerHTML={{ __html: costChart.html }} /></div>
                <div className="totline">
                  Total in view: <b>{inr(costChart.ct)}</b> spent &middot; <b>{costChart.st.toLocaleString()}</b> members still active
                  &middot; Men <b>{inr(costChart.cm)}</b> / {costChart.sm.toLocaleString()}
                  &middot; Women <b>{inr(costChart.cf)}</b> / {costChart.sf.toLocaleString()}
                  {costChart.at > 0 && (
                    <> &middot; that money originally bought <b>{costChart.at.toLocaleString()}</b> members
                      ({inr(costChart.ao)} each, {Math.round((100 * costChart.st) / costChart.at)}% still here)
                    </>
                  )}
                </div>
                {/* The pro-rating caveat that used to sit here was removed on request. The
                    50/50 note below is a DIFFERENT disclosure and is gated on its own
                    condition — gating it on `cu > 0` as before would now paint an empty
                    amber bar on the daily view, where cu is non-zero but noRatioDays is 0. */}
                {costChart.noRatioDays > 0 && (
                  <div className="totline warn">
                    {costChart.noRatioDays} day{costChart.noRatioDays === 1 ? '' : 's'} in view had no gender-targeted
                    spend at all to apportion the untargeted spend against, and fell back to an even 50/50 split.
                  </div>
                )}
              </div>
            )}

            {/* Cohort retention — mockup (11)'s panel, its three toggles and its wording.
                One sentence is appended to each blurb defining what "approved" measures
                here (the account status, visible on search right now), because it is
                narrower than the admin-wide Active total (Approved + Hidden). */}
            {cohortRows && cohortRows.length > 0 && (
              <div className="panel">
                <div className="phead">
                  <h2>Activation Cohort Retention</h2>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Pills options={[['o', 'Overall'], ['m', 'Men'], ['f', 'Women']]} value={cohortGender} onChange={setCohortGender} />
                    <Pills options={[['act', 'By activation date'], ['sign', 'By signup date']]} value={cohortBasis} onChange={setCohortBasis} />
                    <Pills options={[['n', 'Count'], ['p', '%']]} value={cohortView} onChange={setCohortView} />
                    <span className="pmeta">LAST {cohortRows.length} DAILY COHORTS</span>
                  </div>
                </div>
                <div className="psub">
                  {cohortBasis === 'sign' ? (
                    <>
                      Each row is <b>everyone who signed up that day</b> (the campaign view). A cell shows how many
                      of them were active on that later day — the count can <b>rise</b> at first as ghost signups
                      complete their profile late, then attrition takes over. Percentages are of signups. Click any
                      cell: the movement summary appears below and the full member-level list (who dropped, who was
                      added) opens in a new tab. The Overall / Men / Women toggle filters the whole table to one
                      gender.
                    </>
                  ) : (
                    <>
                      Each row is the members who <b>completed signup and activated their profile</b> on that day —
                      incomplete signups are not counted. Reading across: how many were still active 1, 2, 3… days
                      later. Percentages are of the activated cohort. Click any cell: the movement summary appears
                      below and the full member-level list (who dropped, who was added) opens in a new tab. The
                      Overall / Men / Women toggle filters the whole table to one gender.
                    </>
                  )}
                </div>
                <div className="cwrap">
                  <CohortTable
                    rows={cohortRows}
                    basis={cohortBasis}
                    pct={cohortView === 'p'}
                    onCell={onCell}
                    recordedFrom={DB.recording && DB.recording.fromDay}
                  />
                </div>
                <CellDetail detail={detail} />
              </div>
            )}

            {/* Attrition curves — omitted when the source predates the `attr` field. */}
            {attrSvg && (
              <div className="panel">
                <div className="phead">
                  <h2>Attrition &mdash; Overall &amp; Men vs Women</h2>
                  <span className="pmeta">SHARE STILL ACTIVE &middot; DAYS SINCE ACTIVATION</span>
                </div>
                {/* Day 0 is NOT 100% here, and that is the point — see the a0 note in the
                    controller. The old curve opened at a definitional 100 and appeared to
                    lose half the cohort overnight; most of that was members who were never
                    in the approved bucket at all. Saying so on screen is what stops the
                    opening drop being read as churn. */}
                <div className="psub">
                  Averaged across the last {DB.coh.act.o.length} activation cohorts, as a share of everyone who
                  completed their profile that day. <b>Day&nbsp;0 is under 100% on purpose</b>: only
                  {' '}{DB.attr.o[0] == null ? '—' : `${DB.attr.o[0]}%`} of a cohort is actually approved on its own
                  activation day &mdash; the rest are already hidden, suspended, or back to an incomplete profile.
                  The fall from there is real attrition. Dashed grey = everyone; blue = men; rose = women.
                </div>
                <AttritionVerdict attr={DB.attr} />
                <div className="chartwrap"><div style={{ marginTop: 14 }} dangerouslySetInnerHTML={{ __html: attrSvg }} /></div>
                <div className="tblwrap"><AttritionTable attr={DB.attr} /></div>
              </div>
            )}
          </>
        )}
      </div>
    </Box>
  );
};

export default ProfileActivations;
